// src/pages/settings/ProfilePage.tsx

import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Lock,
  Menu,
  BookOpen,
  RefreshCw,
  Shield,
  Smartphone,
  CheckCircle,
  ShieldOff,
} from 'lucide-react';
import { AxiosError } from 'axios';
import { useAuth, usePermissions } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useChatLayout } from '@/components/layout/ChatLayout';
import { wikiApi } from '@/services/wiki-api';
import { authApi, handleApiError } from '@/services/api';
import type { Theme } from '@/types';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import MfaSetupInline from '@/components/auth/MfaSetupInline';
import toast from 'react-hot-toast';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  timezone: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

const ProfilePage: React.FC = () => {
  const { user, workspaceRole, refetch, refreshUser } = useAuth();
  const { canInitiateChat } = usePermissions();
  const mfaEnabled = !!user?.mfaEnabled;
  const roleLabel =
    workspaceRole === 'workspace-owner' ? 'Owner'
    : workspaceRole === 'workspace-admin' ? 'Admin'
    : workspaceRole === 'workspace-member' ? 'Member'
    : null;
  const { theme, setTheme } = useTheme();
  const { isMobile, toggleSidebar } = useChatLayout();
  const navigate = useNavigate();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpeningPreferencesWiki, setIsOpeningPreferencesWiki] = useState(false);

  // MFA: `enrolling` reveals the QR + verify flow; `disabling` reveals the confirm-code form.
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState<string | null>(null);
  const [disableSubmitting, setDisableSubmitting] = useState(false);

  const cancelDisable = () => {
    setDisabling(false);
    setDisableCode('');
    setDisableError(null);
  };

  const submitDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disableCode.length !== 6) return;
    try {
      setDisableSubmitting(true);
      setDisableError(null);
      await authApi.mfaDisable(disableCode);
      await refreshUser();
      cancelDisable();
      toast.success('Two-factor authentication disabled');
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 400) {
        setDisableCode('');
        setDisableError("That code didn't match. Try again.");
      } else {
        setDisableError('Could not disable two-factor. Please try again.');
      }
    } finally {
      setDisableSubmitting(false);
    }
  };

  const browserTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  // Profile form
  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      timezone: user?.timezone || browserTimezone,
    },
  });

  // Password form
  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const onProfileSubmit = async (data: ProfileForm) => {
    try {
      setIsLoading(true);
      await authApi.updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        ...(data.timezone ? { timezone: data.timezone } : {}),
      });
      refetch();
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(handleApiError(error) || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPreferencesWiki = async () => {
    if (isOpeningPreferencesWiki) return;
    setIsOpeningPreferencesWiki(true);
    try {
      const tree = await wikiApi.getTree();
      const own = tree.people.find((p) => p.isOwn);
      if (!own) {
        toast.error('Your preferences wiki page could not be found');
        return;
      }
      navigate(`/wiki/${own.pageId}`);
    } catch (error) {
      toast.error(handleApiError(error) || 'Failed to open preferences wiki');
    } finally {
      setIsOpeningPreferencesWiki(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    try {
      setIsLoading(true);
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast.success('Password updated successfully!');
      passwordForm.reset();
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(handleApiError(error) || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  // Full IANA list, computed once. Each label includes the current UTC
  // offset so the user can pick by clock position even if the zone name
  // is unfamiliar.
  const timezones = useMemo(() => {
    const supported =
      typeof Intl.supportedValuesOf === 'function'
        ? Intl.supportedValuesOf('timeZone')
        : ['UTC'];
    const now = new Date();
    return supported
      .map((tz) => {
        let offset = '';
        try {
          const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'shortOffset',
          }).formatToParts(now);
          offset = parts.find((p) => p.type === 'timeZoneName')?.value || '';
        } catch {
          // ignore — unsupported zone on this platform
        }
        return { value: tz, label: offset ? `${tz} (${offset})` : tz };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/settings" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </Link>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Profile Settings
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage your personal information and account preferences
        </p>
      </div>

      {/* Profile Information */}
      <div className="rounded-xl bg-muted border border-border p-6">
        <div className="flex items-center mb-6">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="ml-6">
            <h2 className="text-xl font-semibold text-foreground">
              {user.firstName} {user.lastName}
            </h2>
            <p className="text-muted-foreground">{user.email}</p>
            {roleLabel && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent text-primary mt-2">
                {roleLabel}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="First Name"
              leftIcon={<User className="w-4 h-4 text-muted-foreground" />}
              error={profileForm.formState.errors.firstName?.message}
              {...profileForm.register('firstName')}
            />
            <Input
              label="Last Name"
              leftIcon={<User className="w-4 h-4 text-muted-foreground" />}
              error={profileForm.formState.errors.lastName?.message}
              {...profileForm.register('lastName')}
            />
          </div>

          <Input
            label="Email Address"
            type="email"
            leftIcon={<Mail className="w-4 h-4 text-muted-foreground" />}
            error={profileForm.formState.errors.email?.message}
            {...profileForm.register('email')}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Timezone
            </label>
            <select
              className="block w-full rounded-xl text-sm bg-white dark:bg-white/5 border border-input dark:border-white/10 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors"
              {...profileForm.register('timezone')}
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {/* Security Settings */}
      <div className="rounded-xl bg-muted border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Security
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Lock className="w-5 h-5 text-muted-foreground mr-3" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Password
                </p>
                <p className="text-sm text-muted-foreground">
                  Last updated 3 months ago
                </p>
              </div>
            </div>
            <button
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-input dark:border-white/10 text-foreground hover:bg-muted transition-colors"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
            >
              Change Password
            </button>
          </div>

          {showPasswordForm && (
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="mt-6 p-4 bg-muted rounded-lg space-y-4">
              <Input
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                leftIcon={<Lock className="w-4 h-4 text-muted-foreground" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
                error={passwordForm.formState.errors.currentPassword?.message}
                {...passwordForm.register('currentPassword')}
              />

              <Input
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                leftIcon={<Lock className="w-4 h-4 text-muted-foreground" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
                error={passwordForm.formState.errors.newPassword?.message}
                {...passwordForm.register('newPassword')}
              />

              <Input
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                leftIcon={<Lock className="w-4 h-4 text-muted-foreground" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
                error={passwordForm.formState.errors.confirmPassword?.message}
                {...passwordForm.register('confirmPassword')}
              />

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary disabled:opacity-50 transition-colors"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-input dark:border-white/10 text-foreground hover:bg-muted transition-colors"
                  onClick={() => {
                    setShowPasswordForm(false);
                    passwordForm.reset();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Two-Factor Authentication */}
        <div className="mt-6 pt-6 border-t border-border space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-card rounded-lg">
                <Smartphone className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  Two-Factor Authentication
                </h3>
                <p className="text-sm text-muted-foreground">
                  Add a one-time code from an authenticator app at sign-in
                </p>
              </div>
            </div>
            {mfaEnabled ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success/15 text-success whitespace-nowrap">
                <CheckCircle className="w-3.5 h-3.5" />
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted-foreground/15 text-muted-foreground whitespace-nowrap">
                Not enabled
              </span>
            )}
          </div>

          {/* Enabled — offer disable + reset */}
          {mfaEnabled && !disabling && (
            <>
              <div className="p-4 bg-success/15 border border-success/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <p className="text-sm font-medium text-success">
                    Two-factor authentication is active
                  </p>
                </div>
                <p className="text-sm text-success mt-1">
                  Lost your authenticator? You can{' '}
                  <Link to="/mfa-reset-request" className="underline font-medium">
                    reset it via email
                  </Link>.
                </p>
              </div>
              <Button variant="outline" onClick={() => setDisabling(true)}>
                <ShieldOff className="w-4 h-4 mr-2" />
                Disable two-factor authentication
              </Button>
            </>
          )}

          {/* Enabled — disable confirmation (current code required) */}
          {mfaEnabled && disabling && (
            <form onSubmit={submitDisable} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the current 6-digit code from your authenticator app to turn
                off two-factor authentication.
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={disableCode}
                onChange={(e) => {
                  setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (disableError) setDisableError(null);
                }}
                className="w-full text-center text-2xl tracking-[0.5em] font-mono px-4 py-3 border border-input rounded-lg bg-card text-foreground focus:ring-2 focus:ring-ring focus:border-primary"
                placeholder="000000"
                autoFocus
                aria-invalid={!!disableError}
              />
              {disableError && (
                <p className="text-sm text-destructive" role="alert">
                  {disableError}
                </p>
              )}
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={cancelDisable} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  className="flex-1"
                  isLoading={disableSubmitting}
                  disabled={disableCode.length !== 6}
                >
                  Disable
                </Button>
              </div>
            </form>
          )}

          {/* Not enabled — offer enrolment */}
          {!mfaEnabled && !enrolling && (
            <>
              <div className="p-4 bg-card border border-border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication is optional but strongly recommended. When
                  enabled, you'll enter a one-time code from your authenticator app each
                  time you sign in.
                </p>
              </div>
              <Button onClick={() => setEnrolling(true)}>
                <Shield className="w-4 h-4 mr-2" />
                Enable two-factor authentication
              </Button>
            </>
          )}

          {/* Not enabled — inline enrolment (QR → verify → done) */}
          {!mfaEnabled && enrolling && (
            <div className="rounded-lg bg-card border border-border p-4">
              <MfaSetupInline onComplete={() => setEnrolling(false)} completeLabel="Done" />
            </div>
          )}
        </div>

        {/* Security Tips */}
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3">Security Tips</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">&#10003;</span>
              Use a strong, unique password for your Zamino account
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">&#10003;</span>
              Turn on two-factor authentication for an extra layer of protection
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">&#10003;</span>
              Review your{' '}
              <Link to="/settings/sessions" className="text-primary underline">
                active sessions
              </Link>{' '}
              regularly
            </li>
          </ul>
        </div>
      </div>

      {/* Preferences */}
      <div className="rounded-xl bg-muted border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Preferences
        </h2>

        <div className="space-y-6">
          {/* Theme Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'light', label: 'Light', icon: '☀️' },
                { value: 'dark', label: 'Dark', icon: '🌙' },
                { value: 'system', label: 'System', icon: '💻' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTheme(option.value as Theme)}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    theme === option.value
                      ? 'border-primary bg-accent text-primary'
                      : 'border-input dark:border-white/10 hover:bg-muted'
                  }`}
                >
                  <div className="text-lg mb-1">{option.icon}</div>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Personal preferences wiki review */}
          {canInitiateChat() && (
            <div className="pt-4 border-t border-border">
              <p className="text-sm font-medium text-foreground">
                Personal preferences wiki
              </p>
              <p className="text-sm text-muted-foreground mb-3">
                See what Zamino has stored about how you like the assistant
                to communicate with you. Edit the page directly or discuss
                changes with the AI.
              </p>
              <button
                type="button"
                onClick={handleOpenPreferencesWiki}
                disabled={isOpeningPreferencesWiki}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary text-primary hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
              >
                {isOpeningPreferencesWiki ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <BookOpen className="w-4 h-4" />
                )}
                Open my preferences wiki
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProfilePage;